import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { forumError } from "@/hooks/use-forum";
import { useHiringContext } from "./use-hiring-context";

export function JobHireReview({
  jobId,
  title,
  budget,
  token,
  agentId,
}: {
  jobId: string;
  title: string;
  budget: number | string;
  token: string;
  agentId: string;
}) {
  const context = useHiringContext(new URLSearchParams({ agent: agentId }));
  const cache = useQueryClient();
  const signature = JSON.stringify([jobId, title, budget, token, agentId]);
  const [accepted, setAccepted] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [uncertain, setUncertain] = useState(false);
  const [done, setDone] = useState(false);

  async function check() {
    const current = (await api.get(`/jobs/${encodeURIComponent(jobId)}`)).data
      .data;
    if (
      current.agentId === agentId &&
      [
        "ASSIGNED",
        "IN_PROGRESS",
        "IN_REVIEW",
        "COMPLETED",
        "DISPUTED",
      ].includes(current.status)
    ) {
      setDone(true);
      setUncertain(false);
      await cache.invalidateQueries({ queryKey: ["job", jobId] });
      return true;
    }
    if (current.status !== "OPEN")
      throw new Error(
        "This job is no longer open for assignment. Refresh its current status."
      );
    if (
      current.title !== title ||
      Number(current.budgetUsdc) !== Number(budget) ||
      (current.tokenSymbol ?? "USDC") !== token
    ) {
      setAccepted("");
      await cache.invalidateQueries({ queryKey: ["job", jobId] });
      throw new Error(
        "This job changed since you reviewed it. Review its current details before confirming assignment."
      );
    }
    setUncertain(false);
    return false;
  }

  async function assign() {
    if (!context.ready || accepted !== signature || busy) return;
    setBusy(true);
    setError("");
    try {
      if (await check()) return;
      await api.patch(`/jobs/${encodeURIComponent(jobId)}/assign`, { agentId });
      setDone(true);
      await cache.invalidateQueries({ queryKey: ["job", jobId] });
      await cache.invalidateQueries({ queryKey: ["job-events", jobId] });
    } catch (cause) {
      setError(forumError(cause));
      setUncertain(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-label="Confirm selected agent"
      className="space-y-4 rounded-xl border border-primary/30 bg-primary/[0.04] p-5"
    >
      <h2 className="text-lg font-semibold">Review your selected agent</h2>
      {context.loading && (
        <p className="text-sm text-muted-foreground">
          Checking the agent’s current profile…
        </p>
      )}
      {context.error && (
        <p role="alert" className="text-sm text-destructive">
          {context.error}
        </p>
      )}
      {context.agent && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Link
            className="break-words font-medium text-primary hover:underline"
            to={`/agents/${encodeURIComponent(agentId)}`}
          >
            {context.agent.name}
          </Link>
          <Link
            className="text-sm text-primary hover:underline"
            to={`/proof/agents/${encodeURIComponent(agentId)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Review work evidence ↗
          </Link>
        </div>
      )}
      <p className="break-words text-sm">
        {title} · {budget} {token}
      </p>
      {done ? (
        <p role="status" className="text-sm text-primary">
          Assignment recorded. Review the job’s funding status before work
          begins.
        </p>
      ) : (
        <>
          <p className="text-sm leading-6 text-muted-foreground">
            Confirm this agent for the scope and acceptance criteria shown on
            this job. This action records the assignment; funding remains a
            separate step.
          </p>
          <label className="flex items-start gap-3 text-sm leading-6">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0"
              checked={accepted === signature}
              disabled={!context.ready || busy || uncertain}
              onChange={(event) =>
                setAccepted(event.target.checked ? signature : "")
              }
            />
            <span>
              I reviewed this job and want to assign{" "}
              {context.agent?.name ?? "the selected agent"}.
            </span>
          </label>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {uncertain ? (
              <Button
                disabled={busy}
                variant="outline"
                onClick={async () => {
                  setBusy(true);
                  setError("");
                  try {
                    await check();
                  } catch (cause) {
                    setError(forumError(cause));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {busy ? "Checking…" : "Check assignment status"}
              </Button>
            ) : (
              <Button
                disabled={!context.ready || accepted !== signature || busy}
                onClick={() => void assign()}
              >
                {busy ? "Assigning…" : "Confirm assignment"}
              </Button>
            )}
            <Button variant="ghost" asChild>
              <Link to={`/jobs/${encodeURIComponent(jobId)}`}>
                Keep reviewing candidates
              </Link>
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
